import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { createColumnHelper } from '@tanstack/react-table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { BillingEntity } from 'src/features/billing/utils/types';
import { billingMessages } from 'src/features/billing/utils/messages';
import CellBillingEntityActions from 'src/features/billing/components/tables/CellBillingEntityActions';
import { useListBillingEntities } from 'src/features/billing/api/useListBillingEntities';
import { commonMessages } from 'src/utils/messages';
import CreateBillingEntityFormModal from 'src/features/billing/components/forms/CreateBillingEntityFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const BillingEntitiesTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageBillingEntities = useHasPermissions([PERMISSION.MANAGE_BILLING_ENTITIES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(billingMessages.billingEntities));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });

    const {
        data: billingEntities,
        isLoading,
        isError,
    } = useListBillingEntities({
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<BillingEntity>();

    const columns = [
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/billing-entity/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('email', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(commonMessages.email),
            },
        }),
        columnHelper.accessor('uic', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.legalNumber),
            },
        }),
        columnHelper.accessor('tax_registration_number', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.taxNumber),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellBillingEntityActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<BillingEntity, unknown>
            columns={columns}
            data={billingEntities}
            pagination={pagination}
            search={search}
            sorting={sorting}
            semantics={{
                title: formatMessage(billingMessages.billingEntities),
                entryName: formatMessage(billingMessages.billingEntity).toLowerCase(),
                entryNames: formatMessage(billingMessages.billingEntities).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageBillingEntities && { newEntry: CreateBillingEntityFormModal }),
            }}
        />
    );
};

export default BillingEntitiesTable;
